import { MatchListEntitiesResponseDto } from "@/lib/api/interfaces/matches.interface";
import { useMatchesCreate, useMatchesCreationEntitiesQuery, useMatchesUpdate } from "@/lib/api/requests/matches.requests";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, Loader2, MapPin, Swords, Trophy } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@/lib/toast";
import z from "zod";
import { EntitySelector } from "./components/entity-selector";
import CreateTeamDialog from "../teams/create-dialog";
import CreateOpponentDialog from "./opponents/create-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/ui-components/ui/tabs";
import { cn } from "@/lib/utils/cn";
import { Autocomplete } from "@/util-components/inputs/autocomplete";

import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs";

/**
 * Main dialog component for creating a new match.
 * Wraps the form and data fetching in a Suspense boundary.
 */
const CreateMatchDialog = ({
  open,
  onClose,
  listEntities: listEntitiesProp,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  listEntities?: MatchListEntitiesResponseDto;
}) => {
  // Fetch fresh entities (including opponents)
  const { data: creationEntities, isLoading: isLoadingEntities, refetch } = useMatchesCreationEntitiesQuery({
    enabled: open,
  });

  const onFormSubmit = () => {
    onClose(true);
  };

  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <CreateBaseDialog
      open={open}
      onClose={onClose}
      icon={Swords}
      title="Neues Spiel erstellen"
      description="Füge ein neues Spiel hinzu und trage optional direkt das Ergebnis ein."
      isSaving={isSaving}
      canSave={canSave}
      formId="create-match-form"
    >
      {!isLoadingEntities && creationEntities && onFormSubmit && (
        <CreateMatchForm
          onFormSubmit={onFormSubmit}
          listEntities={creationEntities}
          onClose={() => onClose(false)}
          refetchEntities={refetch}
          setIsSaving={setIsSaving}
          setCanSave={setCanSave}
        />
      )}
      {isLoadingEntities && (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}
    </CreateBaseDialog>
  );
};

/**
 * Form component that handles state, validation and submission logic for creating a new match.
 */
const CreateMatchForm = ({
  onFormSubmit,
  listEntities,
  onClose,
  refetchEntities,
  setIsSaving,
  setCanSave,
}: {
  onFormSubmit: () => void;
  listEntities: any;
  onClose: () => void;
  refetchEntities: () => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutate: createMatch } = useMatchesCreate();
  const { mutate: updateMatch } = useMatchesUpdate();
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);

  const setIsLoading = (val: boolean) => {
    setIsLoadingLocal(val);
    setIsSaving(val);
  };

  const isLoading = isLoadingLocal;
  const [isHomeGame, setIsHomeGame] = useState(true);
  
  // Quick Create Dialogs
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [createOpponentOpen, setCreateOpponentOpen] = useState(false);

  // Check if the selected date is in the past (match has already happened)
  const isDateInPast = (dateValue: string) => {
    if (!dateValue) return false;
    const selected = new Date(dateValue);
    const now = new Date();
    return selected < now;
  };

  const createMatchSchema = z.object({
    belongsToTeamId: z.string().min(1, {
      message: "Mannschaft ist erforderlich",
    }),
    belongsToOpponentId: z.string().min(1, {
      message: "Gegner ist erforderlich",
    }),
    date: z.string().min(1, {
      message: "Datum ist erforderlich",
    }),
    location: z.string().min(1, {
      message: "Ort ist erforderlich",
    }),
    leagueName: z.string().optional(),
    ourScore: z.number().optional(),
    opponentScore: z.number().optional(),
  });

  type CreateMatchSchema = z.infer<typeof createMatchSchema>;

  const form = useForm<CreateMatchSchema>({
    resolver: zodResolver(createMatchSchema),
    defaultValues: {
      belongsToTeamId: "",
      belongsToOpponentId: "",
      date: "",
      location: "",
      ourScore: 0,
      opponentScore: 0,
      leagueName: "",
    },
  });

  const { isValid } = form.formState;
  
  useEffect(() => {
    setCanSave(isValid);
  }, [isValid, setCanSave]);

  const onSubmit = async (data: CreateMatchSchema) => {
    setIsLoading(true);

    const shouldUpdateWithScores =
      isDateInPast(data.date) && (data.ourScore !== undefined || data.opponentScore !== undefined);

    createMatch(
      {
        belongsToTeamId: data.belongsToTeamId,
        belongsToOpponentId: data.belongsToOpponentId,
        date: new Date(data.date).toISOString(),
        location: data.location,
        leagueName: data.leagueName,
        homeOrAway: isHomeGame ? "HOME" : "AWAY",
      },
      {
        onSuccess: (response) => {
          if (shouldUpdateWithScores && response?._id) {
            updateMatch(
              {
                id: response._id,
                data: {
                  ourScore: data.ourScore,
                  opponentScore: data.opponentScore,
                },
              },
              {
                onSuccess: () => {
                  toast.success("Spiel wurde erfolgreich erstellt und Ergebnis hinzugefügt");
                  setIsLoading(false);
                  onFormSubmit();
                },
                onError: (error) => {
                  toast.error("Ergebnis konnte nicht hinzugefügt werden", {
                    description: "Das Spiel wurde erstellt, aber das Ergebnis konnte nicht gespeichert werden.",
                    error,
                  });
                  setIsLoading(false);
                  onFormSubmit();
                },
              }
            );
          } else {
            toast.success("Spiel wurde erfolgreich erstellt");
            setIsLoading(false);
            onFormSubmit();
          }
        },
        onError: (error) => {
          toast.error("Spiel konnte nicht erstellt werden", { error });
          setIsLoading(false);
          onFormSubmit();
        },
      }
    );
  };

  const opponents = listEntities.opponents || [];
  const teams = listEntities.teams || [];

  return (
    <>
      <Form {...form}>
        <form id="create-match-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Home/Away Toggle */}
              <div className="flex justify-center pb-2">
                <Tabs 
                  value={isHomeGame ? "home" : "away"} 
                  onValueChange={(v) => setIsHomeGame(v === "home")}
                  className="w-full max-w-md"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="home">Heimspiel</TabsTrigger>
                    <TabsTrigger value="away">Auswärtsspiel</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div className="flex gap-8 items-start">
                {/* Team Field - Always belongsToTeamId */}
                <div className={cn("w-1/2 transition-all duration-300 ease-in-out", isHomeGame ? "order-1" : "order-2")}>
                  <FormField
                    control={form.control}
                    name="belongsToTeamId"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-1">
                        <EntitySelector
                          label={isHomeGame ? "Heim" : "Gast"}
                          entities={teams}
                          selectedId={field.value}
                          onSelect={field.onChange}
                          isLoading={isLoading}
                          placeholder="Mannschaft wählen..."
                          searchPlaceholder="Mannschaft suchen..."
                          onQuickCreate={() => setCreateTeamOpen(true)}
                          createLabel="Neue Mannschaft"
                        />
                        <FormMessage className="text-center" />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Opponent Field - Always belongsToOpponentId */}
                <div className={cn("w-1/2 transition-all duration-300 ease-in-out", isHomeGame ? "order-2" : "order-1")}>
                  <FormField
                    control={form.control}
                    name="belongsToOpponentId"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-1">
                        <EntitySelector
                          label={isHomeGame ? "Gast" : "Heim"}
                          entities={opponents}
                          selectedId={field.value}
                          onSelect={field.onChange}
                          isLoading={isLoading}
                          placeholder="Gegner wählen..."
                          searchPlaceholder="Gegner suchen..."
                          onQuickCreate={() => setCreateOpponentOpen(true)}
                          createLabel="Neuer Gegner"
                        />
                        <FormMessage className="text-center" />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
                
              <FormField
                  control={form.control}
                  name="leagueName"
                  render={({ field }) => (
                  <FormItem>
                      <FormLabel isOptional={false}>
                      <Trophy className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Wettbewerb
                      </FormLabel>
                      <FormControl>
                        <Autocomplete
                          options={listEntities.leagueRecommendations || []}
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="z.B. Kreisliga A"
                          emptyText = "Keine Vorschläge gefunden. Der neue Wettbewerb wird übernommen."
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                  </FormItem>
                  )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isOptional={false}>
                          <Calendar className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                          Datum
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="2025-01-01" {...field} disabled={isLoading} type="datetime-local" className="h-11" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isOptional={false}>
                          <MapPin className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                          Ort
                      </FormLabel>
                      <FormControl>
                        <Autocomplete
                          options={listEntities.locationRecommendations || []}
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="z.B. Hauptplatz"
                          emptyText = "Keine Vorschläge gefunden. Der neue Ort wird übernommen."
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              {isDateInPast(form.watch("date")) && (
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-2 pb-1 border-b border-gray-100 mb-2">
                       <span className="text-sm font-semibold text-gray-700">Ergebnis eintragen</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="ourScore"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Unsere Tore</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="0"
                              {...field}
                              disabled={isLoading}
                              type="number"
                              onChange={(e) => field.onChange(Number(e.target.value))}
                              className="bg-white text-center font-bold text-lg h-12"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="opponentScore"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">Gegner Tore</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="0"
                              {...field}
                              type="number"
                              disabled={isLoading}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                               className="bg-white text-center font-bold text-lg h-12"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
  </div>
        </form>
      </Form>
      
      {/* Quick Create Dialogs */}
      {createTeamOpen && (
        <CreateTeamDialog 
          open={createTeamOpen} 
          onClose={() => setCreateTeamOpen(false)}
          onCreated={(team) => {
            refetchEntities();
            form.setValue("belongsToTeamId", team._id);
            setCreateTeamOpen(false);
          }}
        />
      )}
      
      {createOpponentOpen && (
        <CreateOpponentDialog 
          open={createOpponentOpen} 
          onClose={() => setCreateOpponentOpen(false)}
          onCreated={(opponent) => {
            refetchEntities();
            form.setValue("belongsToOpponentId", opponent._id);
            setCreateOpponentOpen(false);
          }}
        />
      )}
    </>
  );
};

export default CreateMatchDialog;
